import React from 'react';
import Avatar from '../Avatar';

/**
 * The activity on the purchase that is currently live on an order.
 *
 * Driven by that purchase's events rather than by the order's top-level
 * approvedAt / acceptedAt / revertedAt fields. Those fields describe whichever
 * purchase last wrote them, so on a re-ordered title the timeline used to show
 * an approval and a revert that happened *before* the purchase heading the
 * list. Reading the events makes the ordering correct by construction.
 */
const EVENTS = {
    purchased: { icon: 'fa-shopping-cart', color: 'success', label: 'Purchased by' },
    correction_proposed: { icon: 'fa-check', color: 'success', label: 'Terms defined by' },
    terms_accepted: { icon: 'fa-handshake', color: 'info', label: 'Accepted by' },
    terms_declined: { icon: 'fa-times', color: 'danger', label: 'Declined by' },
    reverted: { icon: 'fa-undo', color: 'danger', label: 'Reverted by' },
    screening_date_amended: { icon: 'fa-calendar', color: 'info', label: 'Screening date amended by' },
    hire_period_expired: { icon: 'fa-hourglass-end', color: 'secondary', label: 'Hire period ended' },
};

const formatDate = value => (value ? new Date(value).toLocaleString() : '');

const PurchaseTimeline = ({ purchase, orderType }) => {
    const events = purchase?.events || [];

    if (!events.length) {
        return (
            <div className="text-white-50 text-center py-4">
                <span className="fas fa-clock-rotate-left me-2" />
                No activity on this purchase yet
            </div>
        );
    }

    return (
        <div className="position-relative">
            {/* Timeline line - ends at last icon center */}
            <div className="position-absolute bg-primary" style={{
                width: '3px',
                left: '10.5px',
                top: '12px',
                bottom: '28px',
                opacity: '0.3',
                zIndex: '0'
            }} />

            {events.map((event, index) => {
                const { icon, color, label } = EVENTS[event.eventType] || {
                    icon: 'fa-circle', color: 'secondary', label: 'Updated by',
                };
                const user = event.performedBy;
                // bac-python's cronjob pushes entries with no performedBy.
                const actor = user ? `${user.firstName} ${user.lastName}` : 'System';
                const heading = event.eventType === 'purchased' && orderType === 'request'
                    ? 'Requested by'
                    : label;
                const amended = event.eventType === 'screening_date_amended' && event.changes?.screeningDate;

                return (
                    <div key={event._id || index} className="d-flex mb-2 position-relative" style={{ zIndex: '1' }}>
                        <div className="flex-shrink-0 me-2">
                            <div
                                className={`bg-${color} rounded-circle d-flex align-items-center justify-content-center shadow-sm`}
                                style={{ width: '24px', height: '24px', position: 'relative', zIndex: '2' }}
                            >
                                <span className={`fas ${icon} text-white`} style={{ fontSize: '0.7rem' }} />
                            </div>
                        </div>

                        <div className="flex-shrink-0 me-2">
                            <Avatar imageUrl={user?.profilePic?.imageUrl} name={actor} size={44} />
                        </div>

                        <div className={`flex-grow-1 bg-${color} bg-opacity-10 px-2 py-1 rounded border border-${color} border-opacity-25`}>
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="text-white-50 small">{heading}</div>
                                <div className="text-white-50 small" style={{ fontSize: '0.75rem' }}>
                                    {formatDate(event.timestamp)}
                                </div>
                            </div>

                            <div className="text-white small">
                                {actor}
                                {user?.role && ` (${user.role})`}
                            </div>

                            {amended && (
                                <div className="text-white-50 small" style={{ fontSize: '0.75rem' }}>
                                    {formatDate(amended.from)}
                                    <span className="fas fa-arrow-right mx-2 text-primary" />
                                    {formatDate(amended.to)}
                                </div>
                            )}

                            {event.notes && (
                                <div className="px-2 py-1 bg-black bg-opacity-50 rounded mt-1 text-white small">
                                    {event.notes}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PurchaseTimeline;
